import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "./ThemeContext";
import backendApi from "../services/backendApi";
import { buildHomeNavigationTarget, resolveActiveSiteCode } from "../utils/navigationContext";
import useModuleAccess from "../hooks/useModuleAccess";
import {
    FileEdit,
    UploadCloud,
    BellRing,
    BarChart3,
    Building2,
    Activity,
    Package,
    Users,
    BookOpen,
    ChevronRight,
    Shield,
    Home,
    Wrench,
    Zap,
    TrendingUp,
    AlertTriangle,
    History
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MODULE_ICONS } from './icons/ModuleIcons';

// Type definitions
interface NavigationOption {
    moduleKey: string;
    label: string;
    description: string;
    icon: React.ReactNode;
    action: () => void;
    color: string;
}

interface SiteSummary {
    site_name?: string;
    site_code?: string;
}

const MainSidebar: React.FC = () => {
    const { site } = useParams<{ site: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, isGlobalAdmin } = useAuth();
    const { isDarkMode } = useTheme();

    // State for hover-expand behavior on non-fixed pages
    const [isHovered, setIsHovered] = useState(false);

    // Unified Submenu state: tracks which submenu is currently open via hover or click
    const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
    const [isPinned, setIsPinned] = useState(false);
    const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Clear legacy localStorage keys to ensure clean initial state
    useEffect(() => {
        try {
            localStorage.removeItem('sidebar_expanded_reports');
            localStorage.removeItem('sidebar_expanded_oandm');
            localStorage.removeItem('sidebar_expanded_documentation');
            localStorage.removeItem('sidebar_expanded_simulation');
            localStorage.removeItem('sidebar_expanded_performance_analytics');
        } catch (e) {}

        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const [siteInfo, setSiteInfo] = useState<SiteSummary | null>(null);

    // Check if we are on the Navigate page (fixed open)
    const isNavigatePage = location.pathname.includes('/navigate/');

    // The sidebar is effectively "open" if we are on navigate page OR if hovered
    const isOpen = isNavigatePage || isHovered;

    const roleName = (user?.roleName || user?.role || '').toLowerCase();
    const isAdmin = isGlobalAdmin ||
        ['admin', 'super_admin', 'system_admin', 'company_admin', 'ho_admin', 'site_admin'].includes(roleName);

    const currentSite = resolveActiveSiteCode({
        routeSite: site,
        userSiteCode: user?.siteCode,
        userTenant: user?.tenant,
        preferStoredContext: true
    });

    const homeTarget = buildHomeNavigationTarget({
        user,
        isGlobalAdmin,
        siteCode: currentSite
    });
    const { hasModuleAccess, loading: moduleAccessLoading } = useModuleAccess(currentSite);

    useEffect(() => {
        if (currentSite) {
            localStorage.setItem('currentSite', currentSite);
        }
    }, [currentSite]);

    // Fetch site info (simplified version of NavigationPage logic)
    useEffect(() => {
        let isMounted = true;
        const fetchSiteMeta = async () => {
            if (!currentSite) return;
            try {
                const response = await backendApi.getSites(false, currentSite);
                if (response.success && Array.isArray(response.sites)) {
                    const matchedSite = response.sites.find(
                        (item: any) => item.site_code && item.site_code.toUpperCase() === currentSite
                    ) || response.sites[0];

                    if (matchedSite && isMounted) {
                        setSiteInfo({
                            site_name: matchedSite.site_name,
                            site_code: matchedSite.site_code
                        });
                    }
                }
            } catch (error) {
}
        };
        fetchSiteMeta();
        return () => { isMounted = false; };
    }, [currentSite]);

    const displaySiteLabel = siteInfo?.site_name || siteInfo?.site_code?.toUpperCase() || currentSite || "SITE";

    const navigateIfNeeded = (targetRoute: string) => {
        const currentRoute = `${location.pathname}${location.search}`;
        if (currentRoute === targetRoute) {
            return;
        }
        navigate(targetRoute);
    };

    // Navigation Options
     const allOptionsList: NavigationOption[] = [
        // Show Home/Back button for all dashboard pages
        {
            moduleKey: "home",
            label: "Home",
            description: "Return to main navigation",
            icon: React.createElement(MODULE_ICONS.home, { size: 20 }),
            action: () => navigate(homeTarget),
            color: "home"
        },
        {
            moduleKey: "dashboard",
            label: "Performance Analytics",
            description: "View Live reports and insights",
            icon: React.createElement(MODULE_ICONS.dashboard, { size: 20 }),
            action: () => navigate(`/dashboard/${currentSite}`),
            color: "dashboard"
        },
        {
            moduleKey: "site_information",
            label: "Site Information",
            description: "View plant layout and static details",
            icon: React.createElement(MODULE_ICONS.site_information, { size: 20 }),
            action: () => navigate(`/site-info/${currentSite}`),
            color: "site_information"
        },
        {
            moduleKey: "generation_reports",
            label: "Generation Reports",
            description: "Input daily generation reports",
            icon: React.createElement(MODULE_ICONS.generation_reports, { size: 20 }),
            action: () => navigate(`/entry/${currentSite}`), // Submenu trigger handled in render
            color: "generation_reports"
        },
        {
            moduleKey: "om",
            label: "O&M",
            description: "Operations & Maintenance checklists",
            icon: React.createElement(MODULE_ICONS.om, { size: 20 }),
            action: () => navigate(`/om/${currentSite}`), // Navigate to O&M page
            color: "om"
        },
        {
            moduleKey: "upload",
            label: "Upload Plant Data",
            description: "Import data from spreadsheets",
            icon: React.createElement(MODULE_ICONS.upload, { size: 20 }),
            action: () => navigate(`/upload/${currentSite}`),
            color: "upload"
        },
        {
            moduleKey: "alerts",
            label: "Alerts Portal",
            description: "SCB alerts and insights management",
            icon: React.createElement(MODULE_ICONS.alerts, { size: 20 }),
            action: () => navigate(`/alerts/${currentSite}`),
            color: "alerts",
        },
        {
            moduleKey: "simulation",
            label: "Simulation-on-Demand",
            description: "Advanced analytics and simulation tools",
            icon: React.createElement(MODULE_ICONS.simulation, { size: 20 }),
            action: () => navigate(`/simulation/${currentSite}`),
            color: "simulation",
        },
        {
            moduleKey: "dsm",
            label: "DSM",
            description: "Demand Side Management and load analytics",
            icon: React.createElement(MODULE_ICONS.dsm, { size: 20 }),
            action: () => navigate(`/dsm/${currentSite}`),
            color: "dsm",
        },
        {
            moduleKey: "inventory",
            label: "Inventory Management",
            description: "Track equipment and spare parts inventory",
            icon: React.createElement(MODULE_ICONS.inventory, { size: 20 }),
            action: () => navigate(`/inventory/${currentSite}`),
            color: "inventory"
        },
        {
            moduleKey: "vendors",
            label: "Vendor Management",
            description: "Manage supplier relationships and contracts",
            icon: React.createElement(MODULE_ICONS.vendors, { size: 20 }),
            action: () => navigate(`/vendors/${currentSite}`),
            color: "vendors"
        },
        {
            moduleKey: "documentation",
            label: "Documentation",
            description: "WMS PM check sheets and forms",
            icon: React.createElement(MODULE_ICONS.documentation, { size: 20 }),
            action: () => navigate(`/documentation/${currentSite}`),
            color: "documentation"
        },
        {
            moduleKey: "compliance",
            label: "Compliance",
            description: "Manage compliance tracking and reporting",
            icon: React.createElement(MODULE_ICONS.compliance, { size: 20 }),
            action: () => navigate(`/compliance/${currentSite}`),
            color: "compliance"
        }
    ];

    const visibleOptions = allOptionsList.filter((option) =>
        option.moduleKey === 'home' || option.moduleKey === 'site_information' || hasModuleAccess(option.moduleKey)
    );

    const getIconTileClasses = (isActive: boolean) => {
        if (isActive) {
            return 'sidebar-active-tile';
        }
        return 'sidebar-icon-tile';
    };

    const getRowClasses = (isActive: boolean) => {
        if (isActive) {
            return isDarkMode
                ? 'bg-slate-800/80 ring-1 ring-slate-700/80 shadow-sm'
                : 'bg-[#EBF5FB] ring-1 ring-[#BAE6FD]/80 shadow-sm';
        }
        return isDarkMode
            ? 'hover:bg-slate-800/50 text-slate-200'
            : 'hover:bg-[#F2F8FC]/70 text-slate-700';
    };

    const isOptionActive = (option: NavigationOption) => {
        const path = location.pathname;
        switch (option.moduleKey) {
            case "home":
                return path === "/" || path.includes("/navigate");
            case "dashboard":
                return path.includes("/dashboard");
            case "site_information":
                return path.includes("/site-info");
            case "generation_reports":
                return path.includes("/entry");
            case "om":
                return path.includes("/om") || path.includes("/ptw");
            case "upload":
                return path.includes("/upload");
            case "alerts":
                return path.includes("/alerts");
            case "simulation":
                return path.includes("/simulation");
            case "dsm":
                return path.includes("/dsm");
            case "inventory":
                return path.includes("/inventory");
            case "vendors":
                return path.includes("/vendors");
            case "documentation":
                return path.includes("/documentation");
            case "compliance":
                return path.includes("/compliance");
            default:
                return false;
        }
    };

    // Submenu items
    const performanceAnalyticsSheets = [
        { key: "live-2", name: "Live", route: `/dashboard/${currentSite}?tab=live-2` },
        { key: "performance-analytics-2", name: "Performance Analytics", route: `/dashboard/${currentSite}?tab=performance-analytics-2` },
        { key: "forced-outages", name: "Forced Outages", route: `/dashboard/${currentSite}?tab=forced-outages` },
        { key: "historian", name: "Historian", route: `/dashboard/${currentSite}?tab=historian` },
        { key: "virtual-power-plant", name: "Virtual Power Plant", route: `/dashboard/${currentSite}?tab=virtual-power-plant` },
    ];

    const reportSheets = [
        { name: "Daily Report", route: `/entry/${currentSite}?form=Daily Report` },
        { name: "Weather", route: `/entry/${currentSite}?form=Weather` },
        { name: "Inverters", route: `/entry/${currentSite}?form=Inverters` },
        { name: "Breakdowns", route: `/entry/${currentSite}?form=Breakdowns` },
        { name: "Plant Meters", route: `/entry/${currentSite}?form=Plant Meters` }
    ];

    const documentationSheets: Array<{ name: string; route: string }> = [
        { name: "User Manual", route: `/documentation/${currentSite}?type=user-manual` },
        { name: "SLDs", route: `/documentation/${currentSite}?type=slds` },
        { name: "Knowledge", route: `/documentation/${currentSite}?type=knowledge` },
        { name: "Contracts", route: `/documentation/${currentSite}?type=contracts` },
        { name: "Warranty Certificate", route: `/documentation/${currentSite}?type=warranty` }
    ];

    const omSheets = [
        { name: "PM Calendar", route: `/om/${currentSite}` },
        { name: "PTW", route: `/ptw/${currentSite}` },
        { name: "Module Cleaning", route: `/om/${currentSite}?form=module-cleaning` },
        { name: "Grass Cutting", route: `/om/${currentSite}?form=grass-cutting` },
        { name: "Daily Checklist", route: `/om/${currentSite}?form=daily` },
        { name: "Weekly Checklist", route: `/om/${currentSite}?form=weekly` },
        { name: "Monthly Checklist", route: `/om/${currentSite}?form=monthly` },
        { name: "Quarterly Checklist", route: `/om/${currentSite}?form=quarterly` },
        { name: "Yearly Checklist", route: `/om/${currentSite}?form=yearly` }
    ];

    const simulationSheets = [
        { name: "Simulation-On-Demand", route: `/simulation/${currentSite}`, section: 'wallet' as const },
        { name: "Download Raw Data", route: `/simulation/${currentSite}`, section: 'download' as const },
        { name: "Generate Alerts", route: `/simulation/${currentSite}`, section: 'alerts' as const },
        { name: "Run RTC Optimization", route: `/simulation/${currentSite}`, section: 'rtc' as const }
    ];

    const isSubmenuExpanded = (label: string) => {
        return isOpen && activeSubmenu === label;
    };

    const handleMenuMouseEnter = (label: string, hasSub: boolean) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        if (!hasSub) {
            if (!isPinned) {
                hoverTimeoutRef.current = setTimeout(() => {
                    setActiveSubmenu(null);
                }, 100);
            }
            return;
        }

        hoverTimeoutRef.current = setTimeout(() => {
            setActiveSubmenu(label);
            setIsPinned(false);
        }, 120);
    };

    const handleMenuMouseLeave = (label: string, hasSub: boolean) => {
        if (!hasSub) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
        if (!isPinned) {
            hoverTimeoutRef.current = setTimeout(() => {
                setActiveSubmenu((prev) => (prev === label ? null : prev));
            }, 150);
        }
    };

    const handleMenuClick = (option: NavigationOption, hasSub: boolean) => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }

        if (!hasSub) {
            setActiveSubmenu(null);
            setIsPinned(false);
            option.action();
            return;
        }

        if (!isOpen) {
            setIsHovered(true);
        }

        if (activeSubmenu === option.label && isPinned) {
            setActiveSubmenu(null);
            setIsPinned(false);
        } else {
            setActiveSubmenu(option.label);
            setIsPinned(true);
        }

        if (option.label === "Performance Analytics") {
            if (!location.pathname.includes('/dashboard')) {
                navigate(`/dashboard/${currentSite}?tab=live-2`);
            }
        }
    };

    return (
        <>
            {/* Sidebar Container */}
            <motion.div
                onMouseEnter={() => !isNavigatePage && setIsHovered(true)}
                onMouseLeave={() => {
                    if (hoverTimeoutRef.current) {
                        clearTimeout(hoverTimeoutRef.current);
                        hoverTimeoutRef.current = null;
                    }
                    if (!isNavigatePage) {
                        setIsHovered(false);
                    }
                    setActiveSubmenu(null);
                    setIsPinned(false);
                }}
                initial={false}
                animate={{
                    width: isOpen ? "18rem" : "4rem",  // w-72 (18rem) vs 4rem (64px)
                }}
                transition={{ duration: 0.3, type: "spring", stiffness: 200, damping: 25 }}
                className={`main-sidebar-container fixed left-0 top-0 h-screen ${isDarkMode ? 'bg-gradient-to-b from-slate-900/98 via-slate-900/95 to-slate-950/95 border-r border-slate-800 text-slate-100' : 'bg-gradient-to-b from-white/98 via-white/95 to-slate-50/30 border-r border-slate-200/40 text-slate-800'} backdrop-blur-xl shadow-2xl z-[2000] flex flex-col overflow-hidden`}
            >
                {/* Decorative Top Line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0084CF] via-[#0084CF] to-[#3EA3DA]"></div>

                {/* Sidebar Header */}
                <div className={`flex items-center px-2 py-3 border-b ${isDarkMode ? 'border-slate-800' : 'border-slate-200/50'} flex-shrink-0 h-16`}>
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                        <div className="w-1.5 h-6 bg-gradient-to-b from-[#0084CF] to-[#0066A3] rounded-full"></div>
                    </div>

                    {/* Title (Hidden when collapsed, expands smoothly when open) */}
                    <div
                        className="flex items-center flex-1 min-w-0 pl-2.5 pr-2 overflow-hidden transition-all duration-300"
                        style={{
                            opacity: isOpen ? 1 : 0,
                            maxWidth: isOpen ? '200px' : '0px',
                            pointerEvents: isOpen ? 'auto' : 'none',
                        }}
                    >
                        <h2
                            className={`text-xs ${isDarkMode ? 'text-slate-100' : 'text-slate-900'} tracking-tight whitespace-nowrap`}
                            style={{ fontWeight: 900 }}
                        >
                            Quick Access
                        </h2>
                    </div>
                </div>

                {/* User / Profile Section */}
                <div className="py-2 flex-shrink-0 px-2">
                    <motion.div
                        whileHover={isOpen ? { scale: 1.02, translateY: -2 } : {}}
                        className={`relative rounded-2xl overflow-hidden transition-all duration-300 group ${isOpen ? (isDarkMode ? 'bg-slate-800/80 shadow-xl shadow-slate-950/50 ring-1 ring-slate-700' : 'bg-white shadow-xl shadow-slate-200/50 ring-1 ring-slate-100') : 'bg-transparent'}`}
                    >
                        {isOpen && (
                            <>
                                {/* Animated Ambient Background */}
                                <div className="absolute inset-0 opacity-60">
                                    {/* Rotating subtle gradient ring */}
                                    <div className="absolute top-[-50%] left-[-50%] w-[200%] h-[200%] bg-[conic-gradient(from_0deg,transparent_0_340deg,#3EA3DA_360deg)] animate-spin-slow opacity-10"></div>

                                    {/* Floating Blobs */}
                                    <motion.div
                                        animate={{ rotate: 360, scale: [1, 1.2, 1] }}
                                        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                        className={`absolute -top-10 -right-10 w-40 h-40 ${isDarkMode ? 'bg-sky-500/10' : 'bg-slate-200/50'} rounded-full blur-3xl mix-blend-multiply`}
                                    />
                                    <motion.div
                                        animate={{ rotate: -360, scale: [1.2, 1, 1.2] }}
                                        transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                                        className={`absolute -bottom-10 -left-10 w-40 h-40 ${isDarkMode ? 'bg-blue-600/10' : 'bg-slate-300/50'} rounded-full blur-3xl mix-blend-multiply`}
                                    />
                                </div>

                                {/* Glass Overlay */}
                                <div className={`absolute inset-0 ${isDarkMode ? 'bg-slate-900/40' : 'bg-white/40'} backdrop-blur-[1px]`}></div>

                                {/* Shine Effect */}
                                <div className={`absolute inset-0 bg-gradient-to-tr ${isDarkMode ? 'from-white/0 via-white/10 to-white/0' : 'from-white/0 via-white/60 to-white/0'} translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out pointer-events-none`}></div>
                            </>
                        )}

                        <div className="relative z-10 flex items-center h-14 px-0">
                            {/* Avatar Wrapper with Glow - Fixed w-10 h-10 */}
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 relative">
                                {isOpen && (
                                    <div className="absolute -inset-1 bg-gradient-to-r from-[#3EA3DA] to-[#0066A3] rounded-full blur opacity-30 group-hover:opacity-60 transition-opacity duration-500"></div>
                                )}
                                <div className={`relative flex items-center justify-center rounded-full overflow-hidden border-2 ${isDarkMode ? 'border-slate-700' : 'border-white'} shadow-md w-10 h-10`}>
                                    {user?.avatar ? (
                                        <img
                                             src={user.avatar.startsWith('http') || user.avatar.startsWith('data:')
                                                ? user.avatar
                                                : `${backendApi.baseURL.replace('/api', '')}${user.avatar.startsWith('/') ? '' : '/'}${user.avatar}`}
                                            alt="Profile"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).style.display = 'none';
                                                const fallback = e.currentTarget.nextElementSibling;
                                                if (fallback) fallback.classList.remove('hidden');
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center font-bold text-white text-xs bg-gradient-to-br from-slate-500 to-[#0066A3]">
                                            {(user?.fullName || "U").substring(0, 2).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="hidden absolute inset-0 flex items-center justify-center text-xs font-bold text-white bg-slate-700">
                                        {(user?.fullName || "U").substring(0, 2).toUpperCase()}
                                    </div>
                                </div>
                                {/* Online Status Dot */}
                                <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full z-20 shadow-sm shadow-emerald-500/50"></div>
                            </div>

                            {/* User Info - Professional & Concise */}
                            <div
                                className="flex flex-col min-w-0 pl-2.5 pr-2 overflow-hidden transition-all duration-300"
                                style={{
                                    opacity: isOpen ? 1 : 0,
                                    maxWidth: isOpen ? '210px' : '0px',
                                    pointerEvents: isOpen ? 'auto' : 'none',
                                }}
                            >
                                <span className={`text-[11px] font-extrabold uppercase tracking-[0.15em] ${isDarkMode ? 'text-slate-200' : 'text-slate-900'} mb-0.5 whitespace-nowrap truncate`}>
                                    {user?.fullName || "Developer"}
                                </span>
                                <span className={`text-base font-black ${isDarkMode ? 'text-[#38BDF8]' : 'text-slate-900'} leading-tight truncate tracking-wide drop-shadow-sm whitespace-nowrap`}>
                                    {displaySiteLabel}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Scrollable Navigation Items */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden pt-1 pb-1 space-y-1 custom-scrollbar px-2">
                    {moduleAccessLoading ? (
                        Array.from({ length: 6 }).map((_, i) => (
                            <div
                                key={i}
                                className="w-full flex items-center h-11 rounded-xl animate-pulse px-0"
                            >
                                <div className={`w-10 h-10 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'} flex-shrink-0`} />
                                {isOpen && <div className={`h-3 ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'} rounded w-28 ml-3`} />}
                            </div>
                        ))
                    ) : null}
                    {!moduleAccessLoading && visibleOptions.map((option, index) => {
                        const isActive = isOptionActive(option);
                        const hasSubmenu = option.label === "Performance Analytics" || option.label === "Generation Reports" || option.label === "O&M" || option.label === "Documentation" || option.label === "Simulation-on-Demand";
                        const isExpanded = isSubmenuExpanded(option.label);

                        const tileClass = getIconTileClasses(isActive);
                        const rowClass = getRowClasses(isActive);

                        return (
                            <div
                                key={index}
                                className="relative"
                                onMouseEnter={() => handleMenuMouseEnter(option.label, hasSubmenu)}
                                onMouseLeave={() => handleMenuMouseLeave(option.label, hasSubmenu)}
                            >
                                <motion.button
                                    onClick={() => handleMenuClick(option, hasSubmenu)}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.02 }}
                                    whileHover={{ x: isOpen ? 4 : 0 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`w-full flex items-center h-11 rounded-xl ${rowClass} transition-all duration-150 group relative overflow-hidden px-0`}
                                >
                                    {/* Hover gradient effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                                    {/* Stationary Icon Slot: fixed w-10 h-10 */}
                                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                        <div
                                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${tileClass}`}
                                        >
                                            {option.icon}
                                        </div>
                                    </div>

                                    {/* Expanding Label & Chevron: expands to right without moving icon */}
                                    <div
                                        className="flex items-center justify-between flex-1 min-w-0 pl-2.5 pr-2 overflow-hidden transition-all duration-300"
                                        style={{
                                            opacity: isOpen ? 1 : 0,
                                            maxWidth: isOpen ? '210px' : '0px',
                                            pointerEvents: isOpen ? 'auto' : 'none',
                                        }}
                                    >
                                        <span
                                            className={`main-tab-label text-sm leading-tight whitespace-nowrap truncate ${
                                                isActive
                                                    ? (isDarkMode ? 'text-white font-bold main-tab-label-active' : 'text-[#0084CF] font-bold main-tab-label-active')
                                                    : (isDarkMode ? 'text-slate-200 font-semibold group-hover:text-white' : 'text-slate-700 font-semibold group-hover:text-slate-900')
                                            }`}
                                            style={{
                                                fontWeight: isActive ? 600 : 500,
                                                fontFamily: "'IBM Plex Sans', sans-serif",
                                            }}
                                        >
                                            {option.label}
                                        </span>

                                        {hasSubmenu ? (
                                            <motion.div
                                                animate={{ rotate: isExpanded ? 90 : 0 }}
                                                transition={{ duration: 0.2 }}
                                                className="flex-shrink-0 ml-1"
                                            >
                                                <ChevronRight size={14} className={`${isActive ? (isDarkMode ? 'text-slate-300' : 'text-[#0084CF]') : (isDarkMode ? 'text-slate-500 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-700')} transition-colors`} />
                                            </motion.div>
                                        ) : (
                                            <ChevronRight size={14} className={`${isActive ? (isDarkMode ? 'text-slate-300' : 'text-[#0084CF]') : (isDarkMode ? 'text-slate-500 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-700')} transition-colors flex-shrink-0 ml-1`} />
                                        )}
                                    </div>
                                </motion.button>

                                {/* Submenus - Only show when OPEN */}
                                <AnimatePresence>
                                    {isExpanded && option.label === "Performance Analytics" && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-4 mt-1 space-y-1">
                                                {performanceAnalyticsSheets.map((sheet, i) => {
                                                    const isDashboard = location.pathname.includes('/dashboard');
                                                    const searchParams = new URLSearchParams(location.search);
                                                    const currentTabParam = searchParams.get('tab') || searchParams.get('category');
                                                    const isSubActive = isDashboard && (
                                                        currentTabParam === sheet.key ||
                                                        (!currentTabParam && sheet.key === 'live-2')
                                                    );

                                                    return (
                                                        <motion.button
                                                            key={i}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveSubmenu(null);
                                                                setIsPinned(false);
                                                                navigateIfNeeded(sheet.route);
                                                            }}
                                                            whileHover={{ x: 2 }}
                                                            className={`w-full text-left p-2 pl-4 rounded-lg transition-all duration-200 group flex items-center gap-2 ${
                                                                isSubActive 
                                                                    ? (isDarkMode ? 'bg-[#0084CF]/25 text-[#38BDF8]' : 'bg-[#E3F0F9] text-[#0084CF]') 
                                                                    : (isDarkMode ? 'hover:bg-slate-800/70 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600')
                                                            }`}
                                                        >
                                                            <div className="w-2 h-2 bg-[#0084CF] rounded-full"></div>
                                                            <span
                                                                className="text-sm whitespace-nowrap"
                                                                style={{ fontWeight: isSubActive ? 600 : 500, fontFamily: "'IBM Plex Sans', sans-serif" }}
                                                            >
                                                                {sheet.name}
                                                            </span>
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                    {isExpanded && option.label === "Generation Reports" && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-4 mt-1 space-y-1">
                                                {reportSheets.map((sheet, i) => {
                                                    const isEntry = location.pathname.includes('/entry');
                                                    const searchParams = new URLSearchParams(location.search);
                                                    const currentFormParam = searchParams.get('form') || 'Daily Report';
                                                    const isSubActive = isEntry && (currentFormParam === sheet.name);

                                                    return (
                                                        <motion.button
                                                            key={i}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveSubmenu(null);
                                                                setIsPinned(false);
                                                                navigateIfNeeded(sheet.route);
                                                            }}
                                                            whileHover={{ x: 2 }}
                                                            className={`w-full text-left p-2 pl-4 rounded-lg transition-all duration-200 group flex items-center gap-2 ${
                                                                isSubActive 
                                                                    ? (isDarkMode ? 'bg-[#0084CF]/25 text-[#38BDF8]' : 'bg-[#E3F0F9] text-[#0084CF]') 
                                                                    : (isDarkMode ? 'hover:bg-slate-800/70 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600')
                                                            }`}
                                                        >
                                                            <div className={`w-2 h-2 rounded-full ${isSubActive ? 'bg-[#0084CF]' : (isDarkMode ? 'bg-slate-600' : 'bg-slate-400')}`}></div>
                                                            <span
                                                                className="text-sm whitespace-nowrap"
                                                                style={{ fontWeight: isSubActive ? 600 : 500, fontFamily: "'IBM Plex Sans', sans-serif" }}
                                                            >
                                                                {sheet.name}
                                                            </span>
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}

                                    {isExpanded && option.label === "O&M" && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-4 mt-1 space-y-1">
                                                {omSheets.map((sheet, i) => (
                                                    <motion.button
                                                        key={i}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveSubmenu(null);
                                                            setIsPinned(false);
                                                            navigate(sheet.route);
                                                        }}
                                                        whileHover={{ x: 2 }}
                                                        className={`w-full text-left p-2 pl-4 rounded-lg ${isDarkMode ? 'hover:bg-slate-800/70 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600'} transition-all duration-200 group flex items-center gap-2`}
                                                    >
                                                        <div className="w-2 h-2 bg-[#0066A3] rounded-full"></div>
                                                        <span className="text-sm whitespace-nowrap" style={{ fontWeight: 500, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                                                            {sheet.name}
                                                        </span>
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {isExpanded && option.label === "Documentation" && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-4 mt-1 space-y-1">
                                                {documentationSheets.map((sheet, i) => (
                                                    <motion.button
                                                        key={i}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveSubmenu(null);
                                                            setIsPinned(false);
                                                            navigate(sheet.route);
                                                        }}
                                                        whileHover={{ x: 2 }}
                                                        className={`w-full text-left p-2 pl-4 rounded-lg ${isDarkMode ? 'hover:bg-slate-800/70 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600'} transition-all duration-200 group flex items-center gap-2`}
                                                    >
                                                        <div className="w-2 h-2 bg-[#0084CF] rounded-full"></div>
                                                        <span className="text-sm whitespace-nowrap" style={{ fontWeight: 500, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                                                            {sheet.name}
                                                        </span>
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}

                                    {isExpanded && option.label === "Simulation-on-Demand" && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="ml-4 mt-1 space-y-1">
                                                {simulationSheets.map((sheet, i) => (
                                                    <motion.button
                                                        key={i}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveSubmenu(null);
                                                            setIsPinned(false);
                                                            navigate(sheet.route, { state: { simulationSection: sheet.section } });
                                                        }}
                                                        whileHover={{ x: 2 }}
                                                        className={`w-full text-left p-2 pl-4 rounded-lg ${isDarkMode ? 'hover:bg-slate-800/70 text-slate-300 hover:text-white' : 'hover:bg-slate-100 text-slate-600'} transition-all duration-200 group flex items-center gap-2`}
                                                    >
                                                        <div className="w-2 h-2 bg-[#8AC4E6] rounded-full"></div>
                                                        <span className="text-sm whitespace-nowrap" style={{ fontWeight: 500, fontFamily: "'IBM Plex Sans', sans-serif" }}>
                                                            {sheet.name}
                                                        </span>
                                                    </motion.button>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>

                {/* Footer / Admin Link */}
                {isAdmin && (
                    <div className={`py-2 border-t ${isDarkMode ? 'border-slate-800 bg-slate-900/80' : 'border-slate-200/50 bg-gradient-to-t from-slate-50/50 to-transparent'} flex-shrink-0 px-2`}>
                        <motion.button
                            onClick={() => navigate('/admin')}
                            whileHover={{ x: isOpen ? 4 : 0 }}
                            whileTap={{ scale: 0.98 }}
                            className={`w-full text-left rounded-xl main-sidebar-admin-btn bg-gradient-to-r ${isDarkMode ? 'from-slate-800 to-slate-900 hover:from-slate-750 hover:to-slate-800 border border-[#1E3A5F]' : 'from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 border border-slate-200/50'} transition-all duration-200 group relative overflow-hidden flex items-center h-12 px-0`}
                        >
                            {/* Shimmer effect */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>

                            {/* Stationary Icon Slot: fixed w-10 h-10 */}
                            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                                <motion.div
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.5 }}
                                    className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                                >
                                    <img src="/12376087.png" alt="Admin Panel" className="w-6 h-6 object-contain" />
                                </motion.div>
                            </div>

                            {/* Expanding Label & Chevron */}
                            <div
                                className="flex items-center justify-between flex-1 min-w-0 pl-2.5 pr-2 overflow-hidden transition-all duration-300"
                                style={{
                                    opacity: isOpen ? 1 : 0,
                                    maxWidth: isOpen ? '210px' : '0px',
                                    pointerEvents: isOpen ? 'auto' : 'none',
                                }}
                            >
                                <span className={`text-sm font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-800'} tracking-tight whitespace-nowrap truncate`} style={{ fontWeight: 800 }}>
                                    Admin Dashboard
                                </span>
                                <ChevronRight size={14} className={`${isDarkMode ? 'text-slate-500 group-hover:text-slate-300' : 'text-slate-400 group-hover:text-slate-700'} transition-colors flex-shrink-0 ml-1`} />
                            </div>
                        </motion.button>
                    </div>
                )}

            </motion.div>
            <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #0084CF; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #0066A3; }
      `}</style>
        </>
    );
};

export default MainSidebar;

